import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { createWebhook } from '../../api/discord';
import type { DiscordWebhook } from '../../types/discord';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (webhook: DiscordWebhook) => void;
}

export function AddWebhookModal({ open, onClose, onCreated }: Props) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [channel, setChannel] = useState('');
  const [server, setServer] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  function reset() {
    setName('');
    setUrl('');
    setChannel('');
    setServer('');
    setError('');
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSave() {
    setError('');
    setSaving(true);
    try {
      const webhook = await createWebhook({ name, url, channel, server });
      reset();
      onCreated(webhook);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Failed to add webhook');
    } finally {
      setSaving(false);
    }
  }

  const valid = name.trim() && url.trim() && channel.trim() && server.trim();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Discord Webhook</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            autoFocus
          />
          <TextField
            label="Webhook URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            size="small"
            placeholder="https://discord.com/api/webhooks/..."
          />
          <TextField
            label="Channel"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            size="small"
            placeholder="#general"
          />
          <TextField
            label="Server"
            value={server}
            onChange={(e) => setServer(e.target.value)}
            size="small"
          />
          {error && (
            <TextField
              value={error}
              size="small"
              InputProps={{ readOnly: true }}
              sx={{ '& .MuiInputBase-input': { color: 'error.main' } }}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!valid || saving}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
